import React, { useState, useEffect, useRef } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
  Animated,
  RefreshControl,
  StatusBar,
  Alert,
  Image,
  Modal,
  TextInput,
  FlatList,
  SectionList,
  ActivityIndicator,
  BackHandler,
  Linking,
  Share,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  AntDesign,
  MaterialIcons,
  Feather,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width, height } = Dimensions.get('window');
// ===== API Configuration =====
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const salesApi = axios.create({ baseURL: API_BASE_URL, timeout: 15000, headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': API_KEY } });
salesApi.interceptors.request.use(async c => { try { const t = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token'); if (t) c.headers.Authorization = `Bearer ${t}`; } catch {} return c; });
const getBusinessId = async () => { try { return await AsyncStorage.getItem('company_id') || await AsyncStorage.getItem('company_id') || '1'; } catch { return '0'; } };
const safeApiData = r => (r?.data?.success && Array.isArray(r.data.data)) ? r.data.data : [];
const EP = {
  companyProfile: (id) => `/api/data/company_profiles?company_id=${id}`,
  products: '/api/data/sales_products',
  services: '/api/data/sales_services',
  projects: '/api/data/projects',
  pipeline: '/api/data/sales_pipeline',
  activities: '/api/data/sales_activities',
  deals: '/api/data/sales_deals',
  leads: '/api/data/sales_leads',
  clients: '/api/data/clients',
  invoices: '/api/data/invoices',
  teamMembers: '/api/data/team_members',
};
// ===== End API Configuration =====


// Default export for the CompanySalesDepartmentScreen component
export default function CompanySalesDepartmentScreen({ navigation }) {
  // Core state management
  const [companyId, setCompanyId] = useState(null);
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // UI state management
  const [activeTab, setActiveTab] = useState('overview');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSalesSolutions, setShowSalesSolutions] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  
  // Sales data state
  const [salesStats, setSalesStats] = useState({
    totalRevenue: 485000,
    activeDeals: 24,
    avgDealSize: 15200,
    conversionRate: 68,
    products: 45,
    services: 18,
    projects: 12,
    pipeline: 156000,
    closedDeals: 89,
    newLeads: 34,
    followUps: 16,
    proposals: 8
  });

  // Products state
  const [products, setProducts] = useState([
    {
      id: '1',
      name: 'Construction Management Software',
      category: 'Software',
      price: 2500,
      description: 'Complete project management solution for construction companies',
      inventory: 50,
      status: 'active',
      sales: 23
    },
    {
      id: '2',
      name: 'Safety Equipment Package',
      category: 'Equipment',
      price: 850,
      description: 'Comprehensive safety equipment for construction sites',
      inventory: 120,
      status: 'active',
      sales: 67
    },
    {
      id: '3',
      name: 'Building Materials Bundle',
      category: 'Materials',
      price: 1200,
      description: 'Essential building materials for small to medium projects',
      inventory: 85,
      status: 'active',
      sales: 45
    },
    {
      id: '4',
      name: 'Quality Assurance Service',
      category: 'Service',
      price: 3500,
      description: 'Professional quality assurance and inspection services',
      inventory: 0,
      status: 'active',
      sales: 12
    }
  ]);

  // Services state
  const [services, setServices] = useState([
    {
      id: '1',
      name: 'Project Consultation',
      category: 'Consulting',
      price: 150,
      description: 'Expert consultation for construction projects',
      duration: '2 hours',
      status: 'active',
      bookings: 34
    },
    {
      id: '2',
      name: 'Site Inspection',
      category: 'Inspection',
      price: 300,
      description: 'Comprehensive site inspection and reporting',
      duration: '4 hours',
      status: 'active',
      bookings: 28
    },
    {
      id: '3',
      name: 'Training Workshop',
      category: 'Training',
      price: 500,
      description: 'Safety and compliance training for construction teams',
      duration: '1 day',
      status: 'active',
      bookings: 15
    }
  ]);

  // Projects state
  const [projects, setProjects] = useState([
    {
      id: '1',
      name: 'Office Complex Development',
      client: 'ABC Corporation',
      value: 2500000,
      status: 'active',
      progress: 65,
      startDate: '2024-01-15',
      endDate: '2024-12-15',
      team: 12
    },
    {
      id: '2',
      name: 'Residential Tower',
      client: 'XYZ Developers',
      value: 1800000,
      status: 'planning',
      progress: 25,
      startDate: '2024-03-01',
      endDate: '2025-02-28',
      team: 8
    },
    {
      id: '3',
      name: 'Shopping Mall Renovation',
      client: 'Retail Group Ltd',
      value: 950000,
      status: 'completed',
      progress: 100,
      startDate: '2023-08-01',
      endDate: '2024-01-31',
      team: 6
    }
  ]);

  // Pipeline state
  const [pipeline, setPipeline] = useState([
    {
      id: '1',
      prospect: 'Tech Innovations Ltd',
      value: 450000,
      stage: 'Proposal',
      probability: 75,
      expectedClose: '2024-02-15',
      contact: 'Sarah Johnson',
      lastActivity: '2024-01-18'
    },
    {
      id: '2',
      prospect: 'Green Energy Corp',
      value: 320000,
      stage: 'Negotiation',
      probability: 60,
      expectedClose: '2024-02-28',
      contact: 'Mike Chen',
      lastActivity: '2024-01-17'
    },
    {
      id: '3',
      prospect: 'Urban Development Co',
      value: 780000,
      stage: 'Qualified',
      probability: 40,
      expectedClose: '2024-03-15',
      contact: 'Lisa Wang',
      lastActivity: '2024-01-16'
    }
  ]);

  // Recent activities
  const [recentActivities, setRecentActivities] = useState([
    {
      id: '1',
      type: 'sale',
      title: 'New product sale',
      description: 'Construction Management Software sold to BuildCorp',
      amount: 2500,
      timestamp: '2024-01-18T10:30:00Z'
    },
    {
      id: '2',
      type: 'meeting',
      title: 'Client meeting scheduled',
      description: 'Meeting with ABC Corporation for project review',
      timestamp: '2024-01-18T09:15:00Z'
    },
    {
      id: '3',
      type: 'proposal',
      title: 'Proposal submitted',
      description: 'Submitted proposal for Office Complex Development',
      amount: 2500000,
      timestamp: '2024-01-17T16:45:00Z'
    }
  ]);

  // Notifications
  const [notifications, setNotifications] = useState([
    {
      id: '1',
      title: 'New Lead',
      message: 'New lead from website contact form',
      type: 'lead',
      timestamp: '2024-01-18T11:00:00Z',
      read: false
    },
    {
      id: '2',
      title: 'Deal Update',
      message: 'Tech Innovations Ltd moved to negotiation stage',
      type: 'deal',
      timestamp: '2024-01-18T10:30:00Z',
      read: false
    },
    {
      id: '3',
      title: 'Payment Received',
      message: 'Payment received for Safety Equipment Package',
      type: 'payment',
      timestamp: '2024-01-18T09:15:00Z',
      read: true
    }
  ]);

  // All sales solutions data
  const [allSalesSolutions] = useState([
    {
      category: 'Lead Management',
      icon: 'account-plus',
      color: '#3B82F6',
      solutions: [
        { title: 'Lead Capture', icon: 'account-plus', screen: 'LeadCaptureScreen', description: 'Capture new leads' },
        { title: 'Lead Scoring', icon: 'star-check', screen: 'LeadScoringScreen', description: 'Score lead quality' },
        { title: 'Lead Nurturing', icon: 'account-heart', screen: 'LeadNurturingScreen', description: 'Nurture prospects' },
        { title: 'Lead Assignment', icon: 'account-arrow-right', screen: 'LeadAssignmentScreen', description: 'Assign to team' }
      ]
    },
    {
      category: 'Sales Pipeline',
      icon: 'chart-timeline-variant',
      color: '#10B981',
      solutions: [
        { title: 'Opportunity Management', icon: 'briefcase-check', screen: 'OpportunityScreen', description: 'Manage opportunities' },
        { title: 'Pipeline Tracking', icon: 'chart-timeline-variant', screen: 'PipelineTrackingScreen', description: 'Track pipeline' },
        { title: 'Stage Management', icon: 'format-list-numbered', screen: 'StageManagementScreen', description: 'Manage stages' },
        { title: 'Forecasting', icon: 'crystal-ball', screen: 'ForecastingScreen', description: 'Sales forecasting' }
      ]
    },
    {
      category: 'Customer Relations',
      icon: 'account-group',
      color: '#8B5CF6',
      solutions: [
        { title: 'Contact Management', icon: 'contacts', screen: 'ContactManagementScreen', description: 'Manage contacts' },
        { title: 'Communication Hub', icon: 'message-text', screen: 'CommunicationHubScreen', description: 'Communication center' },
        { title: 'Customer Support', icon: 'headset', screen: 'CustomerSupportScreen', description: 'Support customers' },
        { title: 'Feedback System', icon: 'comment-quote', screen: 'FeedbackSystemScreen', description: 'Collect feedback' }
      ]
    },
    {
      category: 'Sales Analytics',
      icon: 'chart-line',
      color: '#F59E0B',
      solutions: [
        { title: 'Sales Dashboard', icon: 'view-dashboard', screen: 'SalesDashboardScreen', description: 'Sales overview' },
        { title: 'Performance Metrics', icon: 'speedometer', screen: 'PerformanceMetricsScreen', description: 'Track performance' },
        { title: 'Revenue Analysis', icon: 'currency-usd', screen: 'RevenueAnalysisScreen', description: 'Analyze revenue' },
        { title: 'Team Analytics', icon: 'account-group-outline', screen: 'TeamAnalyticsScreen', description: 'Team insights' }
      ]
    },
    {
      category: 'Sales Tools',
      icon: 'toolbox',
      color: '#EF4444',
      solutions: [
        { title: 'Quote Generator', icon: 'file-document-edit', screen: 'QuoteGeneratorScreen', description: 'Generate quotes' },
        { title: 'Proposal Builder', icon: 'file-plus', screen: 'ProposalBuilderScreen', description: 'Build proposals' },
        { title: 'Contract Management', icon: 'file-sign', screen: 'ContractManagementScreen', description: 'Manage contracts' },
        { title: 'Invoice System', icon: 'receipt', screen: 'InvoiceSystemScreen', description: 'Create invoices' }
      ]
    },
    {
      category: 'Advanced Features',
      icon: 'rocket-launch',
      color: '#6366F1',
      solutions: [
        { title: 'AI Insights', icon: 'brain', screen: 'AIInsightsScreen', description: 'AI-powered insights' },
        { title: 'Automation Rules', icon: 'robot', screen: 'AutomationRulesScreen', description: 'Automate processes' },
        { title: 'Integration Hub', icon: 'connection', screen: 'IntegrationHubScreen', description: 'Connect systems' },
        { title: 'Mobile Sales', icon: 'cellphone', screen: 'MobileSalesScreen', description: 'Mobile sales tools' }
      ]
    }
  ]);

  // Animation values
  const slideAnim = useRef(new Animated.Value(0)).current;
  const fadeAnim = useRef(new Animated.Value(0)).current;

  // Initialize component
  useEffect(() => {
    initializeScreen();
    setupAnimations();
    
    const backHandler = BackHandler.addEventListener('hardwareBackPress', handleBackPress);
    return () => backHandler.remove();
  }, []);

  const initializeScreen = async () => {
    try {
      setIsLoading(true);
      const bizId = await getBusinessId();
      setCompanyId(bizId);
      await loadUserData(bizId);
      await loadSalesData(bizId);
    } catch (error) {
      console.error('Error initializing screen:', error);
      Alert.alert('Error', 'Failed to load sales data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserData = async (bizId) => {
    try {
      const cid = parseInt(bizId || companyId, 10) || 0;
      // Try API for company profile
      try {
        const profileRes = await salesApi.get(EP.companyProfile(cid));
        const profile = profileRes?.data?.success
          ? (Array.isArray(profileRes.data.data) ? profileRes.data.data[0] : profileRes.data.data)
          : null;
        if (profile) {
          setCompanyProfile(profile);
          await AsyncStorage.setItem('companyProfile', JSON.stringify(profile));
        }
      } catch (apiErr) {
        console.warn('Company profile API unavailable:', apiErr.message);
      }
      // Try API for team members
      try {
        const teamRes = await salesApi.get(EP.teamMembers, { params: { company_id: cid, limit: 100 } });
        const teamData = safeApiData(teamRes);
        if (teamData.length > 0) setTeamMembers(teamData);
      } catch {}
      // Fall back to AsyncStorage for anything not loaded
      const userData = await AsyncStorage.getItem('userData');
      const subscriptionData = await AsyncStorage.getItem('subscriptionData');
      const companyData = await AsyncStorage.getItem('companyProfile');
      
      if (userData) setUser(JSON.parse(userData));
      if (subscriptionData) setSubscription(JSON.parse(subscriptionData));
      if (companyData && !companyProfile) setCompanyProfile(JSON.parse(companyData));
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const loadSalesData = async (bizId) => {
    try {
      const cid = parseInt(bizId || companyId, 10) || 0;
      const params = { company_id: cid, limit: 500 };

      // Fetch from API with cache fallback
      const loadEndpoint = async (endpoint, setter, cacheKey) => {
        try {
          const res = await salesApi.get(endpoint, { params });
          const data = safeApiData(res);
          if (data.length > 0) {
            setter(data);
            await AsyncStorage.setItem(cacheKey, JSON.stringify(data));
            return;
          }
        } catch (e) {
          console.warn(`API ${endpoint} unavailable:`, e.message);
        }
        // Fall back to cache
        try {
          const cached = await AsyncStorage.getItem(cacheKey);
          if (cached) setter(JSON.parse(cached));
        } catch {}
      };

      await Promise.allSettled([
        loadEndpoint(EP.products, setProducts, 'salesProducts'),
        loadEndpoint(EP.services, setServices, 'salesServices'),
        loadEndpoint(EP.projects, setProjects, 'salesProjects'),
        loadEndpoint(EP.pipeline, setPipeline, 'salesPipeline'),
        loadEndpoint(EP.activities, setRecentActivities, 'salesActivities'),
      ]);

      // Update stats from API data
      try {
        const dealsRes = await salesApi.get(EP.deals, { params });
        const dealsData = safeApiData(dealsRes);
        if (dealsData.length > 0) {
          const totalRev = dealsData.filter(d => d.status === 'won' || d.status === 'closed').reduce((s, d) => s + parseFloat(d.value || d.amount || 0), 0);
          const pipeVal = dealsData.filter(d => !['won','lost','closed'].includes(d.status)).reduce((s, d) => s + parseFloat(d.value || d.amount || 0), 0);
          const closed = dealsData.filter(d => d.status === 'won' || d.status === 'closed').length;
          setSalesStats(prev => ({
            ...prev,
            totalRevenue: totalRev || prev.totalRevenue,
            activeDeals: dealsData.filter(d => !['won','lost','closed'].includes(d.status)).length || prev.activeDeals,
            avgDealSize: closed > 0 ? totalRev / closed : prev.avgDealSize,
            pipeline: pipeVal || prev.pipeline,
            closedDeals: closed || prev.closedDeals,
          }));
        }
      } catch {}
    } catch (error) {
      console.error('Error loading sales data:', error);
    }
  };

  // ===== CRUD Operations =====
  const saveRecord = async (endpoint, data, cacheKey, setter, currentData) => {
    try {
      const payload = { ...data, company_id: parseInt(companyId) || 0 };
      const res = await salesApi.post(endpoint, payload);
      const newItem = res.data?.data || { ...payload, id: Date.now() };
      const updated = [newItem, ...currentData];
      setter(updated);
      await AsyncStorage.setItem(cacheKey, JSON.stringify(updated));
      Alert.alert('Success', 'Record created successfully');
      return true;
    } catch (e) {
      console.warn('API create failed:', e.message);
      const newItem = { ...data, id: `local_${Date.now()}`, company_id: parseInt(companyId) || 0, created_at: new Date().toISOString(), status: 'draft' };
      const updated = [newItem, ...currentData];
      setter(updated);
      await AsyncStorage.setItem(cacheKey, JSON.stringify(updated));
      Alert.alert('Saved Locally', 'Will sync when online');
      return true;
    }
  };

  const updateRecord = async (endpoint, id, data, cacheKey, setter, currentData) => {
    try {
      await salesApi.put(`${endpoint}/${id}`, { ...data, company_id: parseInt(companyId) || 0 });
    } catch (e) { console.warn('API update failed:', e.message); }
    const updated = currentData.map(item => String(item.id) === String(id) ? { ...item, ...data, updated_at: new Date().toISOString() } : item);
    setter(updated);
    await AsyncStorage.setItem(cacheKey, JSON.stringify(updated));
    Alert.alert('Success', 'Record updated');
  };

  const deleteRecord = async (endpoint, id, cacheKey, setter, currentData, label) => {
    Alert.alert('Delete', `Delete this ${label}?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: async () => {
        try { await salesApi.delete(`${endpoint}/${id}`); } catch {}
        const updated = currentData.filter(item => String(item.id) !== String(id));
        setter(updated);
        await AsyncStorage.setItem(cacheKey, JSON.stringify(updated));
      }},
    ]);
  };

  // Convenience CRUD for each data type
  const saveProduct = (data) => saveRecord(EP.products, data, 'salesProducts', setProducts, products);
  const updateProduct = (id, data) => updateRecord(EP.products, id, data, 'salesProducts', setProducts, products);
  const deleteProduct = (id) => deleteRecord(EP.products, id, 'salesProducts', setProducts, products, 'Product');
  const saveService = (data) => saveRecord(EP.services, data, 'salesServices', setServices, services);
  const updateService = (id, data) => updateRecord(EP.services, id, data, 'salesServices', setServices, services);
  const deleteService = (id) => deleteRecord(EP.services, id, 'salesServices', setServices, services, 'Service');
  const saveProject = (data) => saveRecord(EP.projects, data, 'salesProjects', setProjects, projects);
  const updateProject = (id, data) => updateRecord(EP.projects, id, data, 'salesProjects', setProjects, projects);
  const deleteProject = (id) => deleteRecord(EP.projects, id, 'salesProjects', setProjects, projects, 'Project');

  const setupAnimations = () => {
    Animated.parallel([
      Animated.timing(slideAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 1000,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const handleBackPress = () => {
    if (showNotifications || showQuickActions || showTeamModal || showSearchModal || showFormModal) {
      setShowNotifications(false);
      setShowQuickActions(false);
      setShowTeamModal(false);
      setShowSearchModal(false);
      setShowFormModal(false);
      return true;
    }
    return false;
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadSalesData(companyId);
    setRefreshing(false);
  };

  // Navigation functions
  const navigateToScreen = (screenName) => {
    if (navigation && navigation.navigate) {
      navigation.navigate(screenName);
    } else {
      Alert.alert('Navigation', `Would navigate to ${screenName}`);
    }
  };

  const openFormModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    setShowFormModal(true);
  };

  // Utility functions
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: companyProfile?.currency || 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': '#10B981',
      'inactive': '#6B7280',
      'pending': '#F59E0B',
      'completed': '#3B82F6',
      'planning': '#8B5CF6'
    };
    return colors[status] || '#6B7280';
  };

  const getStageColor = (stage) => {
    const colors = {
      'Lead': '#6B7280',
      'Qualified': '#3B82F6',
      'Proposal': '#F59E0B',
      'Negotiation': '#EF4444',
      'Closed Won': '#10B981',
      'Closed Lost': '#EF4444'
    };
    return colors[stage] || '#6B7280';
  };

  // Render header
  const renderHeader = () => (
    <LinearGradient
      colors={['#1F2937', '#374151']}
      style={styles.header}
    >
      <View style={styles.headerContent}>
        <TouchableOpacity 
          onPress={() => navigation?.goBack()}
          style={styles.backButton}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        
        <View style={styles.headerCenter}>
          <Text style={styles.headerTitle}>{companyProfile?.name || user?.company_name || 'Sales Department'}</Text>
          <Text style={styles.headerSubtitle}>{companyProfile?.industry ? `${companyProfile.industry} · ` : ''}Sales Department{companyProfile?.headquarters ? ` · ${companyProfile.headquarters}` : ''}</Text>
        </View>
        
        <View style={styles.headerActions}>
          <TouchableOpacity 
            style={styles.headerButton}
            onPress={() => setShowSearchModal(true)}
          >
            <MaterialCommunityIcons name="magnify" size={24} color="#FFFFFF" />
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.headerButton}
            onPress={() => setShowNotifications(true)}
          >
            <MaterialCommunityIcons name="bell" size={24} color="#FFFFFF" />
            {notifications.filter(n => !n.read).length > 0 && (
              <View style={styles.notificationBadge}>
                <Text style={styles.notificationCount}>
                  {notifications.filter(n => !n.read).length}
                </Text>
              </View>
            )}
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.headerButton}
            onPress={() => setShowQuickActions(true)}
          >
            <MaterialCommunityIcons name="plus" size={24} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </View>
    </LinearGradient>
  );

  // Render tabs
  const renderTabs = () => (
    <View style={styles.tabsContainer}>
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.tabsContent}
      >
        {[
          { key: 'overview', title: 'Overview', icon: 'view-dashboard' },
          { key: 'products', title: 'Products', icon: 'package-variant' },
          { key: 'services', title: 'Services', icon: 'cog' },
          { key: 'projects', title: 'Projects', icon: 'briefcase' },
          { key: 'pipeline', title: 'Pipeline', icon: 'chart-timeline-variant' },
          { key: 'solutions', title: 'Solutions', icon: 'apps' }
        ].map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[
              styles.tab,
              activeTab === tab.key && styles.activeTab
            ]}
            onPress={() => setActiveTab(tab.key)}
          >
            <MaterialCommunityIcons 
              name={tab.icon} 
              size={20} 
              color={activeTab === tab.key ? '#3B82F6' : '#6B7280'} 
            />
            <Text style={[
              styles.tabText,
              activeTab === tab.key && styles.activeTabText
            ]}>
              {tab.title}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  // Render overview dashboard with 2-column FlatList
  const renderOverview = () => {
    // Metrics data for FlatList
    const metricsData = [
      {
        id: 'revenue',
        icon: 'currency-usd',
        value: formatCurrency(salesStats.totalRevenue),
        label: 'Total Revenue',
        colors: ['#10B981', '#059669']
      },
      {
        id: 'deals',
        icon: 'handshake',
        value: salesStats.activeDeals,
        label: 'Active Deals',
        colors: ['#3B82F6', '#2563EB']
      },
      {
        id: 'avgDeal',
        icon: 'calculator',
        value: formatCurrency(salesStats.avgDealSize),
        label: 'Avg Deal Size',
        colors: ['#8B5CF6', '#7C3AED']
      },
      {
        id: 'conversion',
        icon: 'percent',
        value: `${salesStats.conversionRate}%`,
        label: 'Conversion Rate',
        colors: ['#F59E0B', '#D97706']
      }
    ];

    // Quick actions data for FlatList
    const actionsData = [
      {
        id: 'product',
        icon: 'package-variant-plus',
        title: 'Add Product',
        subtitle: 'New product to catalog',
        color: '#3B82F6',
        onPress: () => openFormModal('product')
      },
      {
        id: 'service',
        icon: 'cog-outline',
        title: 'Add Service',
        subtitle: 'New service offering',
        color: '#10B981',
        onPress: () => openFormModal('service')
      },
      {
        id: 'project',
        icon: 'briefcase-plus',
        title: 'Add Project',
        subtitle: 'New project opportunity',
        color: '#8B5CF6',
        onPress: () => openFormModal('project')
      },
      {
        id: 'lead',
        icon: 'account-plus',
        title: 'Add Lead',
        subtitle: 'New sales lead',
        color: '#F59E0B',
        onPress: () => openFormModal('lead')
      }
    ];

    const renderMetricItem = ({ item, index }) => (
      <View style={[styles.metricCard, { marginRight: index % 2 === 0 ? 8 : 0 }]}>
        <LinearGradient
          colors={item.colors}
          style={styles.metricGradient}
        >
          <MaterialCommunityIcons name={item.icon} size={24} color="#FFFFFF" />
          <Text style={styles.metricValue}>{item.value}</Text>
          <Text style={styles.metricLabel}>{item.label}</Text>
        </LinearGradient>
      </View>
    );

    const renderActionItem = ({ item, index }) => (
      <TouchableOpacity 
        style={[styles.actionCard, { marginRight: index % 2 === 0 ? 8 : 0 }]}
        onPress={item.onPress}
      >
        <MaterialCommunityIcons name={item.icon} size={32} color={item.color} />
        <Text style={styles.actionTitle}>{item.title}</Text>
        <Text style={styles.actionSubtitle}>{item.subtitle}</Text>
      </TouchableOpacity>
    );

    return (
      <ScrollView 
        style={styles.content}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={['#3B82F6']}
          />
        }
        showsVerticalScrollIndicator={false}
      >
        {/* Sales Overview */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Sales Overview</Text>
          
          <FlatList
            data={metricsData}
            keyExtractor={(item) => item.id}
            numColumns={2}
            columnWrapperStyle={styles.row}
            renderItem={renderMetricItem}
            scrollEnabled={false}
            showsVerticalScrollIndicator={false}
          />
        </View>

        {/* Quick Actions */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Quick Actions</Text>
          
          <FlatList
            data={actionsData}
            keyExtractor={(item) => item.id}
            numColumns={2}
            columnWrapperStyle={styles.row}
            renderItem={renderActionItem}
            scrollEnabled={false}
            showsVerticalScrollIndicator={false}
          />
        </View>

        {/* Recent Activity */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Recent Activity</Text>
            <TouchableOpacity onPress={() => navigateToScreen('ActivityScreen')}>
              <Text style={styles.seeAllText}>See All</Text>
            </TouchableOpacity>
          </View>
          
          {recentActivities.slice(0, 3).map((activity) => (
            <View key={activity.id} style={styles.activityCard}>
              <View style={[styles.activityIcon, { backgroundColor: getActivityColor(activity.type) }]}>
                <MaterialCommunityIcons 
                  name={getActivityIcon(activity.type)} 
                  size={20} 
                  color="#FFFFFF" 
                />
              </View>
              <View style={styles.activityContent}>
                <Text style={styles.activityTitle}>{activity.title}</Text>
                <Text style={styles.activityDescription}>{activity.description}</Text>
                {activity.amount && (
                  <Text style={styles.activityAmount}>{formatCurrency(activity.amount)}</Text>
                )}
              </View>
              <Text style={styles.activityTime}>{formatDate(activity.timestamp)}</Text>
            </View>
          ))}
        </View>
      </ScrollView>
    );
  };

  // Render products (unchanged)
  const renderProducts = () => (
    <ScrollView 
      style={styles.content}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={['#3B82F6']}
        />
      }
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Products</Text>
          <TouchableOpacity 
            style={styles.addButton}
            onPress={() => openFormModal('product')}
          >
            <MaterialCommunityIcons name="plus" size={20} color="#FFFFFF" />
            <Text style={styles.addButtonText}>Add Product</Text>
          </TouchableOpacity>
        </View>
        
        {products.map((product) => (
          <TouchableOpacity 
            key={product.id} 
            style={styles.itemCard}
            onPress={() => openFormModal('product', product)}
          >
            <View style={styles.itemHeader}>
              <View style={styles.itemInfo}>
                <Text style={styles.itemName}>{product.name}</Text>
                <Text style={styles.itemCategory}>{product.category}</Text>
              </View>
              <View style={styles.itemPrice}>
                <Text style={styles.priceText}>{formatCurrency(product.price)}</Text>
                <View style={[styles.statusBadge, { backgroundColor: getStatusColor(product.status) }]}>
                  <Text style={styles.statusText}>{product.status}</Text>
                </View>
              </View>
            </View>
            
            <Text style={styles.itemDescription} numberOfLines={2}>
              {product.description}
            </Text>
            
            <View style={styles.itemFooter}>
              <View style={styles.itemStat}>
                <MaterialCommunityIcons name="package" size={16} color="#6B7280" />
                <Text style={styles.statText}>Stock: {product.inventory}</Text>
              </View>
              <View style={styles.itemStat}>
                <MaterialCommunityIcons name="chart-line" size={16} color="#6B7280" />
                <Text style={styles.statText}>Sales: {product.sales}</Text>
              </View>
            </View>
          </TouchableOpacity>
        ))}
      </View>
    </ScrollView>
  );

  // Render services (unchanged)
  const renderServices = () => (
    <ScrollView 
      style={styles.content}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={['#3B82F6']}
        />
      }
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Services</Text>
          <TouchableOpacity 
            style={styles.addButton}
            onPress={() => openFormModal('service')}
          >
            <MaterialCommunityIcons name="plus" size={20} color="#FFFFFF" />
            <Text style={styles.addButtonText}>Add Service</Text>
          </TouchableOpacity>
        </View>
        
        {services.map((service) => (
          <TouchableOpacity 
            key={service.id} 
            style={styles.itemCard}
            onPress={() => openFormModal('service', service)}
          >
            <View style={styles.itemHeader}>
              <View style={styles.itemInfo}>
                <Text style={styles.itemName}>{service.name}</Text>
                <Text style={styles.itemCategory}>{service.category}</Text>
              </View>
              <View style={styles.itemPrice}>
                <Text style={styles.priceText}>{formatCurrency(service.price)}</Text>
                <View style={[styles.statusBadge, { backgroundColor: getStatusColor(service.status) }]}>
                  <Text style={styles.statusText}>{service.status}</Text>
                </View>
              </View>
            </View>
            
            <Text style={styles.itemDescription} numberOfLines={2}>
              {service.description}
            </Text>
            
            <View style={styles.itemFooter}>
              <View style={styles.itemStat}>
                <MaterialCommunityIcons name="clock" size={16} color="#6B7280" />
                <Text style={styles.statText}>Duration: {service.duration}</Text>
              </View>
              <View style={styles.itemStat}>
                <MaterialCommunityIcons name="calendar-check" size={16} color="#6B7280" />
                <Text style={styles.statText}>Bookings: {service.bookings}</Text>
              </View>
            </View>
          </TouchableOpacity>
        ))}
      </View>
    </ScrollView>
  );

  // Render projects (unchanged)
  const renderProjects = () => (
    <ScrollView 
      style={styles.content}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={['#3B82F6']}
        />
      }
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Projects</Text>
          <TouchableOpacity 
            style={styles.addButton}
            onPress={() => openFormModal('project')}
          >
            <MaterialCommunityIcons name="plus" size={20} color="#FFFFFF" />
            <Text style={styles.addButtonText}>Add Project</Text>
          </TouchableOpacity>
        </View>
        
        {projects.map((project) => (
          <TouchableOpacity 
            key={project.id} 
            style={styles.itemCard}
            onPress={() => openFormModal('project', project)}
          >
            <View style={styles.itemHeader}>
              <View style={styles.itemInfo}>
                <Text style={styles.itemName}>{project.name}</Text>
                <Text style={styles.itemCategory}>{project.client}</Text>
              </View>
              <View style={styles.itemPrice}>
                <Text style={styles.priceText}>{formatCurrency(project.value)}</Text>
                <View style={[styles.statusBadge, { backgroundColor: getStatusColor(project.status) }]}>
                  <Text style={styles.statusText}>{project.status}</Text>
                </View>
              </View>
            </View>
            
            <View style={styles.progressContainer}>
              <View style={styles.progressHeader}>
                <Text style={styles.progressLabel}>Progress</Text>
                <Text style={styles.progressValue}>{project.progress}%</Text>
              </View>
              <View style={styles.progressBar}>
                <View 
                  style={[
                    styles.progressFill, 
                    { width: `${project.progress}%`, backgroundColor: getStatusColor(project.status) }
                  ]} 
                />
              </View>
            </View>
            
            <View style={styles.itemFooter}>
              <View style={styles.itemStat}>
                <MaterialCommunityIcons name="calendar" size={16} color="#6B7280" />
                <Text style={styles.statText}>{formatDate(project.startDate)} - {formatDate(project.endDate)}</Text>
              </View>
              <View style={styles.itemStat}>
                <MaterialCommunityIcons name="account-group" size={16} color="#6B7280" />
                <Text style={styles.statText}>Team: {project.team}</Text>
              </View>
            </View>
          </TouchableOpacity>
        ))}
      </View>
    </ScrollView>
  );

  // Render pipeline (unchanged)
  const renderPipeline = () => (
    <ScrollView 
      style={styles.content}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={['#3B82F6']}
        />
      }
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Sales Pipeline</Text>
          <TouchableOpacity 
            style={styles.addButton}
            onPress={() => openFormModal('opportunity')}
          >
            <MaterialCommunityIcons name="plus" size={20} color="#FFFFFF" />
            <Text style={styles.addButtonText}>Add Opportunity</Text>
          </TouchableOpacity>
        </View>
        
        {pipeline.map((opportunity) => (
          <TouchableOpacity 
            key={opportunity.id} 
            style={styles.itemCard}
            onPress={() => openFormModal('opportunity', opportunity)}
          >
            <View style={styles.itemHeader}>
              <View style={styles.itemInfo}>
                <Text style={styles.itemName}>{opportunity.prospect}</Text>
                <Text style={styles.itemCategory}>{opportunity.contact}</Text>
              </View>
              <View style={styles.itemPrice}>
                <Text style={styles.priceText}>{formatCurrency(opportunity.value)}</Text>
                <View style={[styles.statusBadge, { backgroundColor: getStageColor(opportunity.stage) }]}>
                  <Text style={styles.statusText}>{opportunity.stage}</Text>
                </View>
              </View>
            </View>
            
            <View style={styles.probabilityContainer}>
              <View style={styles.probabilityHeader}>
                <Text style={styles.probabilityLabel}>Probability</Text>
                <Text style={styles.probabilityValue}>{opportunity.probability}%</Text>
              </View>
              <View style={styles.probabilityBar}>
                <View 
                  style={[
                    styles.probabilityFill, 
                    { width: `${opportunity.probability}%` }
                  ]} 
                />
              </View>
            </View>
            
            <View style={styles.itemFooter}>
              <View style={styles.itemStat}>
                <MaterialCommunityIcons name="calendar" size={16} color="#6B7280" />
                <Text style={styles.statText}>Close: {formatDate(opportunity.expectedClose)}</Text>
              </View>
              <View style={styles.itemStat}>
                <MaterialCommunityIcons name="clock" size={16} color="#6B7280" />
                <Text style={styles.statText}>Last: {formatDate(opportunity.lastActivity)}</Text>
              </View>
            </View>
          </TouchableOpacity>
        ))}
      </View>
    </ScrollView>
  );

  // Render sales solutions with 2-column FlatList
  const renderSolutions = () => {
    const renderSolutionCategory = ({ item: category }) => {
      const renderSolutionItem = ({ item: solution, index }) => (
        <TouchableOpacity
          style={[styles.solutionCard, { marginRight: index % 2 === 0 ? 8 : 0 }]}
          onPress={() => navigateToScreen(solution.screen)}
        >
          <MaterialCommunityIcons 
            name={solution.icon} 
            size={28} 
            color={category.color} 
          />
          <Text style={styles.solutionTitle}>{solution.title}</Text>
          <Text style={styles.solutionDescription}>
            {solution.description}
          </Text>
        </TouchableOpacity>
      );

      return (
        <View style={styles.solutionCategory}>
          <View style={styles.categoryHeader}>
            <View style={[styles.categoryIcon, { backgroundColor: category.color }]}>
              <MaterialCommunityIcons 
                name={category.icon} 
                size={24} 
                color="#FFFFFF" 
              />
            </View>
            <Text style={styles.categoryTitle}>{category.category}</Text>
            <Text style={styles.categoryCount}>({category.solutions.length})</Text>
          </View>
          
          <FlatList
            data={category.solutions}
            keyExtractor={(item, index) => `${category.category}-${index}`}
            numColumns={2}
            columnWrapperStyle={styles.row}
            renderItem={renderSolutionItem}
            scrollEnabled={false}
            showsVerticalScrollIndicator={false}
          />
        </View>
      );
    };

    return (
      <ScrollView 
        style={styles.content}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={['#3B82F6']}
          />
        }
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Sales Solutions</Text>
          <Text style={styles.sectionSubtitle}>
            Comprehensive tools to manage your sales operations
          </Text>
          
          <FlatList
            data={allSalesSolutions}
            keyExtractor={(item, index) => `category-${index}`}
            renderItem={renderSolutionCategory}
            scrollEnabled={false}
            showsVerticalScrollIndicator={false}
          />
        </View>
      </ScrollView>
    );
  };

  // Render content based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'products':
        return renderProducts();
      case 'services':
        return renderServices();
      case 'projects':
        return renderProjects();
      case 'pipeline':
        return renderPipeline();
      case 'solutions':
        return renderSolutions();
      default:
        return renderOverview();
    }
  };

  // Render form modal (unchanged)
  const renderFormModal = () => (
    <Modal
      visible={showFormModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowFormModal(false)}
    >
      <View style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>
            <Text>{editingItem ? 'Edit' : 'Add'} {modalType.charAt(0).toUpperCase() + modalType.slice(1)}</Text>
          </Text>
          <TouchableOpacity 
            style={styles.modalCloseButton}
            onPress={() => setShowFormModal(false)}
          >
            <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
          </TouchableOpacity>
        </View>
        
        <ScrollView style={styles.modalContent}>
          <Text style={styles.modalText}>
            Form for {modalType} would be implemented here with appropriate fields.
          </Text>
        </ScrollView>
      </View>
    </Modal>
  );

  // Helper functions
  const getActivityColor = (type) => {
    const colors = {
      'sale': '#10B981',
      'meeting': '#3B82F6',
      'proposal': '#F59E0B',
      'call': '#8B5CF6'
    };
    return colors[type] || '#6B7280';
  };

  const getActivityIcon = (type) => {
    const icons = {
      'sale': 'currency-usd',
      'meeting': 'calendar',
      'proposal': 'file-document',
      'call': 'phone'
    };
    return icons[type] || 'information';
  };

  // Loading state
  if (isLoading) {
    return (
      <SafeAreaView style={styles.loadingContainer}>
        <ActivityIndicator size={36} color="#3B82F6" />
        <Text style={styles.loadingText}>Loading Sales Department...</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#1F2937" />
      
      {renderHeader()}
      {renderTabs()}
      {renderContent()}
      {renderFormModal()}
    </SafeAreaView>
  );
}

// Styles
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#4A90E2',
  },
  
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
  },
  
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#6B7280',
    fontWeight: '500',
  },
  
  // Header styles
  header: {
    paddingTop: 12,
    paddingBottom: 16,
    paddingHorizontal: 16,
  },
  
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#4A90E2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  
  headerButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#4A90E2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 8,
    position: 'relative',
  },
  
  notificationBadge: {
    position: 'absolute',
    top: -2,
    right: -2,
    backgroundColor: '#EF4444',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  notificationCount: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  
  // Tabs styles
  tabsContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  tabsContent: {
    paddingHorizontal: 16,
  },
  
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 8,
    borderRadius: 20,
  },
  
  activeTab: {
    backgroundColor: '#EBF4FF',
  },
  
  tabText: {
    fontSize: 14,
    color: '#6B7280',
    marginLeft: 8,
    fontWeight: '500',
  },
  
  activeTabText: {
    color: '#3B82F6',
    fontWeight: '600',
  },
  
  // Content styles
  content: {
    flex: 1,
    padding: 16,
  },
  
  section: {
    marginBottom: 24,
  },
  
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#4A90E2',
    marginBottom: 4,
  },
  
  sectionSubtitle: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 16,
  },
  
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  
  seeAllText: {
    fontSize: 14,
    color: '#3B82F6',
    fontWeight: '500',
  },
  
  // Row style for 2-column layout
  row: {
    justifyContent: 'space-between',
  },
  
  // Metrics styles (2-column layout)
  metricCard: {
    width: (width - 48) / 2,
    marginVertical: 6,
    borderRadius: 12,
    overflow: 'hidden',
  },
  
  metricGradient: {
    padding: 16,
    alignItems: 'center',
  },
  
  metricValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginTop: 8,
  },
  
  metricLabel: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.9)',
    marginTop: 4,
    textAlign: 'center',
  },
  
  // Actions styles (2-column layout)
  actionCard: {
    width: (width - 48) / 2,
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
    padding: 16,
    marginVertical: 6,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  actionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginTop: 8,
    textAlign: 'center',
  },
  
  actionSubtitle: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 4,
    textAlign: 'center',
  },
  
  // Activity styles
  activityCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  activityIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  
  activityContent: {
    flex: 1,
  },
  
  activityTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  activityDescription: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  
  activityAmount: {
    fontSize: 12,
    fontWeight: '600',
    color: '#10B981',
    marginTop: 2,
  },
  
  activityTime: {
    fontSize: 12,
    color: '#6B7280',
  },
  
  // Item card styles
  itemCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  itemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  
  itemInfo: {
    flex: 1,
  },
  
  itemName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  itemCategory: {
    fontSize: 14,
    color: '#6B7280',
    marginTop: 2,
  },
  
  itemPrice: {
    alignItems: 'flex-end',
  },
  
  priceText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#10B981',
    marginBottom: 4,
  },
  
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  
  itemDescription: {
    fontSize: 14,
    color: '#374151',
    lineHeight: 20,
    marginBottom: 12,
  },
  
  itemFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  
  itemStat: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  
  statText: {
    fontSize: 12,
    color: '#6B7280',
    marginLeft: 4,
  },
  
  // Progress styles
  progressContainer: {
    marginBottom: 12,
  },
  
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  
  progressLabel: {
    fontSize: 12,
    color: '#6B7280',
  },
  
  progressValue: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  progressBar: {
    height: 6,
    backgroundColor: '#E5E7EB',
    borderRadius: 3,
    overflow: 'hidden',
  },
  
  progressFill: {
    height: '100%',
    borderRadius: 3,
  },
  
  // Probability styles
  probabilityContainer: {
    marginBottom: 12,
  },
  
  probabilityHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  
  probabilityLabel: {
    fontSize: 12,
    color: '#6B7280',
  },
  
  probabilityValue: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  probabilityBar: {
    height: 6,
    backgroundColor: '#E5E7EB',
    borderRadius: 3,
    overflow: 'hidden',
  },
  
  probabilityFill: {
    height: '100%',
    backgroundColor: '#3B82F6',
    borderRadius: 3,
  },
  
  // Add button styles
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#3B82F6',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
  },
  
  addButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 4,
  },
  
  // Solutions styles (2-column layout)
  solutionCategory: {
    marginBottom: 24,
  },
  
  categoryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  
  categoryIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  
  categoryTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    flex: 1,
  },
  
  categoryCount: {
    fontSize: 14,
    color: '#FFFFFF',
  },
  
  solutionCard: {
    width: (width - 48) / 2,
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
    padding: 16,
    marginVertical: 6,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  solutionTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1F2937',
    marginTop: 8,
    textAlign: 'center',
  },
  
  solutionDescription: {
    fontSize: 10,
    color: '#6B7280',
    marginTop: 4,
    textAlign: 'center',
  },
  
  // Modal styles
  modalContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#4A90E2',
  },
  
  modalCloseButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  modalContent: {
    flex: 1,
    padding: 16,
  },
  
  modalText: {
    fontSize: 16,
    color: '#374151',
    textAlign: 'center',
    marginTop: 40,
  },
});